<template>
    <Teleport to="body">
        <div
            v-if="show"
            class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
            @click.self="maybeClose"
        >
                <div class="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
                    <h2 class="text-lg font-bold mb-1">AI Analysis</h2>
                    <p class="text-sm text-gray-500 mb-4">
                        Analyze <span class="font-medium text-gray-700">{{ video.title }}</span> to
                        automatically detect plays, create clips, and build a playbook.
                    </p>

                    <!-- idle -->
                    <div v-if="phase === 'idle'">
                        <ul class="list-disc list-inside mt-1 space-y-1 text-gray-600 text-sm mb-4">
                            <li>Detect plays in the video using AI</li>
                            <li>Add each clip to <strong>{{ videoGroup?.name ?? 'this Video Group' }}</strong></li>
                            <li>Create a new playbook named <strong>{{ videoGroup?.name ?? 'AI Analysis' }}</strong></li>
                            <li>Create a whiteboard play for each clip</li>
                        </ul>
                        <label class="flex items-center gap-2 text-sm text-gray-700 mb-5 cursor-pointer select-none">
                            <input type="checkbox" v-model="cutClips" class="rounded" />
                            Cut individual clip files (downloads full video + runs FFmpeg)
                        </label>
                        <div class="flex gap-3 justify-end">
                            <button @click="close" class="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-100">
                                Cancel
                            </button>
                            <button @click="start" class="px-4 py-2 text-sm rounded-lg bg-primary text-white hover:opacity-90">
                                Run AI Analysis
                            </button>
                        </div>
                    </div>

                    <!-- running -->
                    <div v-else-if="phase === 'running'" class="py-2">
                        <!-- spinner + message -->
                        <div class="flex items-center gap-3 mb-4">
                            <svg class="animate-spin h-6 w-6 text-primary flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                            </svg>
                            <span class="text-sm text-gray-600">{{ statusMessage }}</span>
                        </div>

                        <!-- progress bar -->
                        <div class="w-full bg-gray-200 rounded-full h-2 mb-3">
                            <div
                                class="bg-primary h-2 rounded-full transition-all duration-500"
                                :style="{ width: Math.round(progress * 100) + '%' }"
                            ></div>
                        </div>
                        <div class="flex justify-between text-xs text-gray-400 mb-4">
                            <span>{{ Math.round(progress * 100) }}%</span>
                            <span v-if="clipsCreated > 0">{{ clipsCreated }} clip{{ clipsCreated !== 1 ? 's' : '' }} created</span>
                        </div>

                        <!-- live clip list -->
                        <div v-if="liveClips.length" class="bg-gray-50 rounded-lg p-3 max-h-32 overflow-y-auto space-y-1 mb-4">
                            <div v-for="clip in liveClips" :key="clip.clip_id" class="flex items-center gap-2 text-sm">
                                <ion-icon name="film-outline" class="text-primary flex-shrink-0"></ion-icon>
                                <span class="text-gray-700">{{ clip.name }}</span>
                            </div>
                        </div>
                    </div>

                    <!-- complete -->
                    <div v-else-if="phase === 'complete'" class="py-2">
                        <div class="flex items-center gap-2 text-green-600 mb-3">
                            <ion-icon name="checkmark-circle-outline" class="text-2xl"></ion-icon>
                            <span class="font-semibold">Analysis complete</span>
                        </div>
                        <p class="text-sm text-gray-600 mb-4">
                            Created <strong>{{ clipsCreated }}</strong> clip{{ clipsCreated !== 1 ? 's' : '' }}
                            <span v-if="playbookId"> in playbook <strong>{{ videoGroup?.name ?? 'AI Analysis' }}</strong></span>.
                        </p>
                        <div class="flex justify-end">
                            <button @click="close" class="px-4 py-2 text-sm rounded-lg bg-primary text-white hover:opacity-90">
                                Done
                            </button>
                        </div>
                    </div>

                    <!-- error -->
                    <div v-else-if="phase === 'error'" class="py-2">
                        <div class="flex items-center gap-2 text-red-500 mb-3">
                            <ion-icon name="alert-circle-outline" class="text-2xl"></ion-icon>
                            <span class="font-semibold">Analysis failed</span>
                        </div>
                        <p class="text-sm text-gray-600 mb-4">{{ errorMessage }}</p>
                        <div class="flex gap-3 justify-end">
                            <button @click="close" class="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-100">
                                Close
                            </button>
                            <button @click="start" class="px-4 py-2 text-sm rounded-lg bg-primary text-white hover:opacity-90">
                                Retry
                            </button>
                        </div>
                    </div>
                </div>
        </div>
    </Teleport>
</template>

<script setup>
import { ref, watch, onBeforeUnmount } from 'vue';

const props = defineProps({
    show:       { type: Boolean, default: false },
    video:      { type: Object, required: true },
    videoGroup: { type: Object, default: null },
});

const emit = defineEmits(['close']);

const phase         = ref('idle');
const progress      = ref(0);
const statusMessage = ref('Starting…');
const clipsCreated  = ref(0);
const liveClips     = ref([]);
const playbookId    = ref(null);
const errorMessage  = ref('');
const currentJobId  = ref(null);
const cutClips      = ref(false);

let pollTimer = null;

watch(() => props.show, (val) => {
    if (val) reset();
});

function reset() {
    phase.value         = 'idle';
    progress.value      = 0;
    statusMessage.value = 'Starting…';
    clipsCreated.value  = 0;
    liveClips.value     = [];
    playbookId.value    = null;
    errorMessage.value  = '';
    currentJobId.value  = null;
    cutClips.value      = false;
    stopPolling();
}

function close() {
    stopPolling();
    emit('close');
}

function maybeClose() {
    if (phase.value !== 'running') close();
}

async function start() {
    phase.value         = 'running';
    progress.value      = 0;
    statusMessage.value = 'Submitting…';
    clipsCreated.value  = 0;
    liveClips.value     = [];
    stopPolling();

    try {
        const res = await axios.post(`/api/v1/videos/${props.video.id}/ai-analysis`, { cut_clips: cutClips.value });

        if (res.data.job_id) {
            currentJobId.value  = res.data.job_id;
            statusMessage.value = 'Queued — waiting for worker…';
            startPolling();
        } else {
            handleSyncResult(res.data);
        }
    } catch (err) {
        phase.value        = 'error';
        errorMessage.value = err?.response?.data?.message ?? 'An unexpected error occurred.';
    }
}

function handleSyncResult(data) {
    if (data.clips_created > 0) {
        clipsCreated.value = data.clips_created;
        playbookId.value   = data.playbook_id;
        phase.value        = 'complete';
    } else {
        phase.value        = 'error';
        errorMessage.value = data.message ?? 'No plays were detected in this video.';
    }
}

function startPolling() {
    stopPolling();
    pollTimer = setInterval(poll, 2000);
}

function stopPolling() {
    if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
}

async function poll() {
    if (!currentJobId.value) return;
    try {
        const res = await axios.get(
            `/api/v1/videos/${props.video.id}/ai-analysis/status`,
            { params: { job_id: currentJobId.value } }
        );
        const d = res.data;

        progress.value      = d.progress ?? progress.value;
        statusMessage.value = d.message  ?? statusMessage.value;

        if (d.playbook_id) playbookId.value = d.playbook_id;
        if (d.plays_created) clipsCreated.value = d.plays_created;

        if (d.status === 'complete') {
            stopPolling();
            progress.value      = 1;
            statusMessage.value = 'Analysis complete';
            phase.value         = 'complete';
        } else if (d.status === 'error') {
            stopPolling();
            phase.value        = 'error';
            errorMessage.value = d.error ?? 'Analysis failed.';
        }
    } catch (_) {
        // transient network error — keep polling
    }
}

onBeforeUnmount(() => stopPolling());
</script>
